import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import NotFound from './pages/NotFound';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FocusHandler } from './components/common/FocusHandler';
import { RouterRecoveryMiddleware } from './components/common/RouterRecoveryMiddleware';
import { GlobalLoadingInterceptor } from './components/common/GlobalLoadingInterceptor';
import AppShell from './components/Layout/AppShell';
import TestPage from './pages/TestPage';
import Dashboard from './pages/Dashboard';
import MapView from './pages/MapView';
import CesiumMapView from './pages/CesiumMapView';
import DebugLotMappingPage from './pages/DebugLotMappingPage';
import SubLeases from './pages/SubLeases';
import ReadOnlySubLeases from './pages/SubLeases/ReadOnlySubLeases';
import EnhancedSubLeases from './pages/Enhanced/SubLeases';
import EnhancedLots from './pages/Enhanced/Lots';
import EnhancedUnderLeases from './pages/Enhanced/UnderLeases';
import EnhancedLeaseholders from './pages/Enhanced/Leaseholders';
import EnhancedApplicants from './pages/Enhanced/Applicants';
import EnhancedApplications from './pages/Enhanced/Applications';
import EnhancedFinancials from './pages/Enhanced/Financials';
import EnhancedMeetings from './pages/Enhanced/Meetings';
import EnhancedMarketReviews from './pages/Enhanced/MarketReviews';
import EnhancedLeaseEOIs from './pages/Enhanced/LeaseEOIs';
import EnhancedEOIResponses from './pages/Enhanced/EOIResponses';
import EnhancedProjects from './pages/Enhanced/Projects';
import EnhancedSuppliers from './pages/Enhanced/Suppliers';
import EnhancedRentalContracts from './pages/Enhanced/RentalContracts';
import EnhancedAuditLogs from './pages/Enhanced/AuditLogs';
import EnhancedLayout from './pages/Enhanced/EnhancedLayout';
import UnderLeases from './pages/UnderLeases/index';
import ReadOnlyApplications from './pages/Applications/ReadOnlyApplications';
import UnderLeasesAdmin from './pages/Admin/UnderLeases/UnderLeasesAdmin';
import Leaseholders from './pages/Leaseholders';
import Settings from './pages/Settings';
import Financials from './pages/Financials';
import Applicants from './pages/Applicants';
import LeaseApplications from './pages/LeaseApplications';
import Meetings from './pages/Meetings';
import MarketReviews from './pages/MarketReviews';
import AdminLayout from './pages/Admin/AdminLayout';
import LotsAdmin from './pages/Admin/Lots/LotsAdmin';
import LeasesAdmin from './pages/Admin/Leases/LeasesAdmin';
import SubLeasesAdmin from './pages/Admin/Subleases/SubLeasesAdmin';
import AddSublease from './pages/Admin/Subleases/AddSublease';
import LeaseholdersAdmin from './pages/Admin/Leaseholders/LeaseholdersAdmin';
import ApplicantsAdmin from './pages/Admin/Applicants/ApplicantsAdmin';
import AddApplicant from './pages/Admin/Applicants/AddApplicant';
import ApplicationsAdmin from './pages/Admin/Applications/ApplicationsAdmin';
import AddApplication from './pages/Admin/Applications/AddApplication';
import EOIResponsesAdmin from './pages/Admin/EOIResponses/EOIResponsesAdmin';
import LeaseEOIsAdmin from './pages/Admin/LeaseEOIs/LeaseEOIsAdmin';
import ProjectsAdmin from './pages/Admin/Projects/ProjectsAdmin';
import SuppliersAdmin from './pages/Admin/Suppliers/SuppliersAdmin';
import FinancialsAdmin from './pages/Admin/Financials/FinancialsAdmin';
import MeetingsAdmin from './pages/Admin/Meetings/MeetingsAdmin';
import MarketReviewsAdmin from './pages/Admin/MarketReviews/MarketReviewsAdmin';
import LotSurveyPlansAdmin from './pages/Admin/LotSurveyPlans/LotSurveyPlansAdmin';
import SettingsLayout from './pages/Admin/Settings/SettingsLayout';
import UserManagement from './pages/Admin/Settings/UserManagement';
import Login from './pages/Login';
import Logout from './pages/Logout';
import EmailVerification from './pages/EmailVerification';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './context/AuthContext';
import type { Permission } from './types/auth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, 
      refetchOnWindowFocus: false, // Disabled to prevent automatic refetch storm
      staleTime: 5 * 60 * 1000, // Increased stale time to 5 minutes
      gcTime: 10 * 60 * 1000, // Increased gcTime to 10 minutes for longer cache retention
      refetchOnMount: true, // Changed from 'if-stale' to boolean true
      refetchOnReconnect: true, // Keep this true for network interruptions
    },
  },
});

function LoginRoute() {
  const { user } = useAuth();
  return user ? <Navigate to="/" replace /> : <Login />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <FocusHandler />
          <RouterRecoveryMiddleware />
          <GlobalLoadingInterceptor /> 
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/3d-map" element={<CesiumMapView />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                {/* Dashboard and basic features */}
                <Route index element={<Dashboard />} />
                <Route path="test-page" element={<TestPage />} />
                <Route path="map" element={<MapView />} />
                <Route path="debug-lot-mapping" element={<DebugLotMappingPage />} />
                
                {/* Protected features with specific permissions */}
                <Route element={<ProtectedRoute requiredPermissions={['view_leases']} />}>
                  <Route path="leases" element={<ReadOnlySubLeases />} />
                  <Route path="underleases" element={<UnderLeases />} />
                </Route>
                
                <Route element={<ProtectedRoute requiredPermissions={['view_reports']} />}>
                  <Route path="leaseholders" element={<Leaseholders />} />
                  <Route path="financials" element={<Financials />} />
                </Route>
                
                <Route element={<ProtectedRoute requiredPermissions={['approve_applications']} />}>
                  <Route path="applicants" element={<Applicants />} />
                  <Route path="applications" element={<ReadOnlyApplications />} />
                  <Route path="lease-applications" element={<LeaseApplications />} />
                </Route>
                
                <Route path="meetings" element={<Meetings />} />
                <Route path="market-reviews" element={<MarketReviews />} />
                
                <Route element={<ProtectedRoute requiredPermissions={['manage_system_settings']} />}>
                  <Route path="settings/*" element={<Settings />} />
                </Route>

                {/* Enhanced routes */}
                <Route element={<ProtectedRoute requiredPermissions={['view_leases']} />}>
                  <Route path="enhanced" element={<EnhancedLayout />}>
                  <Route path="lots" element={<EnhancedLots />} />
                  <Route path="sub-leases" element={<TestPage />} />
                  <Route path="under-leases" element={<EnhancedUnderLeases />} />
                  <Route path="leaseholders" element={<EnhancedLeaseholders />} />
                  <Route path="applicants" element={<EnhancedApplicants />} />
                  <Route path="applications" element={<EnhancedApplications />} />
                  <Route path="financials" element={<EnhancedFinancials />} />
                  <Route path="meetings" element={<EnhancedMeetings />} />
                  <Route path="market-reviews" element={<EnhancedMarketReviews />} />
                  <Route path="lease-eois" element={<EnhancedLeaseEOIs />} />
                  <Route path="eoi-responses" element={<EnhancedEOIResponses />} />
                  <Route path="projects" element={<EnhancedProjects />} />
                  <Route path="suppliers" element={<EnhancedSuppliers />} />
                  <Route path="rental-contracts" element={<EnhancedRentalContracts />} />
                  </Route>
                </Route>
                
                <Route element={<ProtectedRoute requiredPermissions={['manage_system_settings']} />}>
                  <Route path="enhanced" element={<EnhancedLayout />}>
                    <Route path="audit-logs" element={<EnhancedAuditLogs />} />
                  </Route>
                </Route>

                {/* Admin routes */}
                <Route element={<ProtectedRoute requiredPermissions={['manage_users', 'manage_system_settings']} />}>
                  <Route path="admin" element={<AdminLayout />}>
                    <Route path="lots" element={<LotsAdmin />} />
                    <Route path="leases" element={<LeasesAdmin />} />
                    <Route path="subleases" element={<SubLeasesAdmin />} />
                    <Route path="leaseholders" element={<LeaseholdersAdmin />} />
                    <Route path="applicants" element={<ApplicantsAdmin />} />
                    <Route path="applicants/add" element={<AddApplicant />} />
                    <Route path="applications" element={<ApplicationsAdmin />} />
                    <Route path="applications/add" element={<AddApplication />} />
                    <Route path="financials" element={<FinancialsAdmin />} />
                    <Route path="meetings" element={<MeetingsAdmin />} />
                    <Route path="market-reviews" element={<MarketReviewsAdmin />} />
                    <Route path="lot-survey-plans" element={<LotSurveyPlansAdmin />} />
                    <Route path="lease-eois" element={<LeaseEOIsAdmin />} />
                    <Route path="eoi-responses" element={<EOIResponsesAdmin />} />
                    <Route path="projects" element={<ProjectsAdmin />} />
                    <Route path="suppliers" element={<SuppliersAdmin />} />
                    <Route path="underleases" element={<UnderLeasesAdmin />} />
                    
                    {/* Settings routes */}
                    <Route path="settings" element={<SettingsLayout />}>
                      <Route path="user-management" element={<UserManagement />} />
                      <Route path="audit-logs" element={<EnhancedAuditLogs />} />
                      <Route path="system" element={<div>System Settings</div>} />
                      <Route index element={<Navigate to="user-management" replace />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>

            {/* 404 page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
